/**
 * profileChange — BACKLOG-1675 (Bill 2026-10-02).
 *
 * An existing customer's profile edits write to Core Director when the Edit
 * Person modal is saved: the server compares the saved person with LIVE Core
 * Director, lists the accounts the customer owns (slots 1-4) with the account
 * fields the change maps to, and the officer confirms — method of
 * verification, initials, which accounts — before anything is written.
 *
 *   POST /v3/customers/{cif}/profile-change/preview
 *   POST /v3/customers/{cif}/profile-change/apply
 *
 * Mirrors apps/PCBRetailApi/Models/CustomerProfileChangeModels.cs.
 */

/** The person as saved. Blank / undefined = untouched. `addr*` is the PHYSICAL
 *  address and `mail*` the MAILING one; the server decides the Core slots. */
export interface ProfileChangePersonInput {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  /** ISO yyyy-MM-dd. */
  dob?: string;
  addr?: string;
  city?: string;
  state?: string;
  zip?: string;
  mailAddr?: string;
  mailCity?: string;
  mailState?: string;
  mailZip?: string;
  useMailingAddr?: boolean;
  phoneHome?: string;
  phoneCell?: string;
  phoneBus?: string;
  email?: string;
  employer?: string;
  occupation?: string;
}

export type ProfileChangeSource =
  | 'rao-person-dialog'
  | 'rcm-edit-cif'
  | 'rcm-change-address'
  | 'rcm-change-contact'
  | 'ram-address-change';

export interface ProfileChangePreviewRequest {
  person: ProfileChangePersonInput;
  source?: ProfileChangeSource;
  cartId?: string;
}

/** One changed CIF field: what Core holds now and what the officer saved. */
export interface ProfileChangeDiffRow {
  field: string;
  label: string;
  /** Core Director destination, e.g. "F908", "F9-12", "PersonName.FirstName". */
  coreField: string;
  from: string;
  to: string;
}

/** One account field the waterfall would write (or deliberately not write). */
export interface ProfileChangeAccountFieldRow {
  field: string;
  label: string;
  from: string;
  to: string;
  write: boolean;
  reason?: string | null;
}

export interface ProfileChangeAccountPlan {
  acctId: string;
  acctType: string;
  typeLabel: string;
  prodDesc?: string | null;
  acctStat?: string | null;
  /** 1-4 when known. */
  ownerSlot?: number | null;
  ownerSlotSource: string;
  relCode?: string | null;
  relDesc?: string | null;
  /** Field 80 "2nd Use Code": 2 = 2nd-name lookup, M = alternate mailing, A = snowbird. */
  useCode80?: string | null;
  eligible: boolean;
  reason?: string | null;
  fields: ProfileChangeAccountFieldRow[];
  selectedByDefault: boolean;
}

export interface ProfileChangeAuthMethod {
  code: string;
  label: string;
  requiresInitials: boolean;
}

export type ProfileChangeMode = 'confirm' | 'commit-time';

export interface ProfileChangePreviewResponse {
  success: boolean;
  cifId: string;
  hasChanges: boolean;
  diff: ProfileChangeDiffRow[];
  accounts: ProfileChangeAccountPlan[];
  authMethods: ProfileChangeAuthMethod[];
  notePreview: string;
  previewToken: string;
  appliedKey: string;
  mode: ProfileChangeMode | string;
  warnings: string[];
  error?: string | null;
  errorCode?: string | null;
}

export interface ProfileChangeAccountRef {
  acctId: string;
  acctType: string;
}

export interface ProfileChangeApplyRequest {
  person: ProfileChangePersonInput;
  authMethodCode: string;
  customerInitials?: string;
  /** The accounts the officer left checked. */
  accounts: ProfileChangeAccountRef[];
  previewToken: string;
  source?: ProfileChangeSource;
  cartId?: string;
}

export interface ProfileChangeStepResult {
  ok: boolean;
  error?: string | null;
}

export interface ProfileChangeAccountResult {
  acctId: string;
  acctType: string;
  ok: boolean;
  /** A re-read of the account showed every written field holding the new value. */
  verified: boolean;
  error?: string | null;
  fieldsWritten: string[];
}

export interface ProfileChangeNoteResult {
  /** "cif" or the account number. */
  target: string;
  ok: boolean;
  error?: string | null;
}

export interface ProfileChangeApplyResponse {
  success: boolean;
  /** False when nothing changed or the CIF write was refused. */
  applied: boolean;
  cifId: string;
  maintActionId?: string | null;
  appliedKey: string;
  custMod?: ProfileChangeStepResult | null;
  accounts: ProfileChangeAccountResult[];
  notes: ProfileChangeNoteResult[];
  warnings: string[];
  error?: string | null;
  errorCode?: string | null;
  replayed?: boolean;
  /** Core Director read back after the CIF write: true = every changed field landed,
   *  false = at least one still shows the old value, null = the read-back failed. */
  custModVerified?: boolean | null;
  /** Labels of changed CIF fields Core Director still shows the old value for. */
  unverifiedFields?: string[];
}

/** Stamped on the cart person's `extra.profileChange` after a successful apply so
 *  the commit pipeline skips the CIF update it would otherwise repeat. */
export interface ProfileChangeStamp {
  appliedAt: string;
  appliedKey: string;
  maintActionId?: string | null;
  authMethod: string;
}

/** SystemSettings key read by the clients to decide whether to show the confirm step. */
export const PROFILE_CHANGE_MODE_KEY = 'profileChange.mode';
